// 账户状态 - Zustand (web 版)
// 业务接口与 desktop 完全一致，但底层用 fetch + SSE 替代 electron IPC
import { create } from 'zustand';
import type {
  Account,
  HeigeEvent,
  MarketStatus,
  StockBasic,
  StockQuote,
} from '../shared/types';
import { api, SSE_CONFIG } from '../lib/api';
import { subscribeHeigeStream, HeigeStreamEvent } from '../lib/sse';

interface NewsHealth {
  status: 'ok' | 'slow' | 'offline';
  lastSuccessAt: number | null;
  lastErrorAt: number | null;
  lastErrorMessage: string | null;
  consecutiveFailures: number;
}

export interface HeigeTriggerResult {
  event: HeigeEvent;
  blockByGuard: boolean;
  affected: {
    code: string;
    name: string;
    shares: number;
    amount: number;
    insurance: 'none' | 'basic' | 'finance';
    policyId?: string;
  }[];
  task2Reward: number;
  totalPayout: number;
}

interface StoreState {
  account: Account | null;
  pool: StockBasic[];
  quotes: Record<string, StockQuote>;
  marketStatus: MarketStatus | null;
  loading: boolean;
  error: string | null;
  lastUpdate: number;
  newsHealth: NewsHealth;

  // Actions
  loadPool: () => Promise<void>;
  loadAccount: () => Promise<void>;
  loadMarketStatus: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  refreshQuotes: (items?: string[] | StockBasic[]) => Promise<StockQuote[]>;
  /** 把用户搜索到的股票加进 pool（合并到默认池，session 内持续存在） */
  addToPool: (stocks: StockBasic[]) => void;
  signin: () => Promise<void>;
  reset: () => Promise<void>;
  buy: (params: any) => Promise<{ ok: boolean; message: string; account?: Account }>;
  sell: (params: any) => Promise<{ ok: boolean; message: string; account?: Account }>;
  buyInsurance: () => Promise<{ ok: boolean; message: string; account?: Account }>;
  buyFinanceInsurance: (tier: 'tier1' | 'tier2' | 'tier3') => Promise<{ ok: boolean; message: string; account?: Account }>;
  buyGuard: () => Promise<{ ok: boolean; message: string; account?: Account }>;
  setAutoInsurance: (params: { enabled: boolean; cashRatio?: number; tier?: any }) => Promise<{ ok: boolean; account?: Account }>;

  // Contract
  openContract: (params: any) => Promise<{ ok: boolean; message: string; contract?: any; account?: Account }>;
  closeContract: (contractId: string, price: number) => Promise<{ ok: boolean; message: string; account?: Account }>;
  setContractTPSL: (contractId: string, tp?: number, sl?: number) => Promise<{ ok: boolean; message: string; account?: Account }>;

  // Heige
  triggerHeige: (body: any) => Promise<HeigeTriggerResult>;
  getHeigeHistory: () => Promise<HeigeEvent[]>;
  pollHeigeFeed: () => Promise<HeigeEvent | null>; // web 端为 SSE 占位（兼容 desktop 接口）
  onHeigeEvent: (cb: (data: { event: HeigeEvent; result: HeigeTriggerResult }) => void) => () => void;

  // Ticker
  startTicker: (intervalMs?: number) => () => void;
  refreshNewsHealth: () => Promise<void>;

  // Web 简化版 storeInfo（云端无路径，但保留字段避免组件报错）
  storeInfo: { path: string; isCustom: boolean; sizeBytes: number } | null;
  loadStoreInfo: () => Promise<void>;
  pickStorePath: () => Promise<string | null>;
  migrateStore: (_dir: string) => Promise<{ ok: boolean; message: string }>;
  resetStorePath: () => Promise<void>;
}

// ============ 内部：股票池（前端硬编码，跟 desktop 一致）============
const DEFAULT_POOL: StockBasic[] = [
  // A 股
  { code: '600519', name: '贵州茅台', market: 'sh', type: 'stock', tag: 'A 股' },
  { code: '601318', name: '中国平安', market: 'sh', type: 'stock', tag: 'A 股' },
  { code: '600036', name: '招商银行', market: 'sh', type: 'stock', tag: 'A 股' },
  { code: '000858', name: '五粮液', market: 'sz', type: 'stock', tag: 'A 股' },
  { code: '000001', name: '平安银行', market: 'sz', type: 'stock', tag: 'A 股' },
  { code: '300750', name: '宁德时代', market: 'sz', type: 'stock', tag: 'A 股' },
  { code: '002594', name: '比亚迪', market: 'sz', type: 'stock', tag: 'A 股' },
  // 加密货币（10 种主流币；MATIC 在 Gate.io 已下架，故去掉换成 TRX/DOT）
  { code: 'BTCUSDT', name: '比特币', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'ETHUSDT', name: '以太坊', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'SOLUSDT', name: 'Solana', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'BNBUSDT', name: '币安币', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'XRPUSDT', name: '瑞波币', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'DOGEUSDT', name: '狗狗币', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'ADAUSDT', name: '卡尔达诺', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'AVAXUSDT', name: '雪崩', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'TRXUSDT', name: '波场', market: 'crypto', type: 'crypto', tag: '加密货币' },
  { code: 'LINKUSDT', name: 'Chainlink', market: 'crypto', type: 'crypto', tag: '加密货币' },
];

/** 包装：调用 api + 同步 account 到 store + try/catch 返回 {ok, message} */
function wrapAction(fn: (...args: any[]) => Promise<any>, set: any) {
  return async (...args: any[]) => {
    try {
      const r: any = await fn(...args);
      if (r?.account) set({ account: r.account });
      return { ok: true, message: r?.message ?? 'ok', ...r };
    } catch (e) {
      return { ok: false, message: (e as Error).message };
    }
  };
}

export const useStore = create<StoreState>((set, get) => ({
  account: null,
  pool: [],
  quotes: {},
  marketStatus: null,
  loading: false,
  error: null,
  lastUpdate: 0,
  newsHealth: {
    status: 'ok',
    lastSuccessAt: null,
    lastErrorAt: null,
    lastErrorMessage: null,
    consecutiveFailures: 0,
  },
  storeInfo: null,

  loadPool: async () => {
    // web 版从后端 /api/pool 拿默认股票池（之前硬编码前端，已迁移）
    try {
      const r = await api.getPool();
      set({ pool: r.pool });
    } catch (e) {
      // 后端拉取失败时降级到硬编码兜底，避免页面空白
      console.warn('[loadPool] 后端拉取失败，使用本地兜底:', (e as Error).message);
      set({ pool: DEFAULT_POOL });
    }
  },

  addToPool: (stocks: StockBasic[]) => {
    if (!stocks.length) return;
    const cur = get().pool;
    const known = new Set(cur.map((s) => s.code));
    const merged = [...cur];
    for (const s of stocks) {
      if (!known.has(s.code)) {
        merged.push(s);
        known.add(s.code);
      }
    }
    if (merged.length !== cur.length) set({ pool: merged });
  },

  loadAccount: async () => {
    try {
      const r = await api.getAccount();
      set({ account: r.account });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  loadMarketStatus: async () => {
    // 客户端基于时间判断（与 desktop 相同的 A 股交易时段逻辑）
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const isWeekday = now.getDay() >= 1 && now.getDay() <= 5;
    const inMorning = minutes >= 9 * 60 + 30 && minutes <= 11 * 60 + 30;
    const inAfternoon = minutes >= 13 * 60 && minutes <= 15 * 60;
    const isOpen = isWeekday && (inMorning || inAfternoon);
    set({
      marketStatus: {
        isOpen,
        label: isOpen ? 'A 股交易中' : 'A 股休市',
        now: Date.now(),
      },
    });
  },

  refreshAccount: async () => {
    try {
      const r = await api.getAccount();
      set({ account: r.account });
    } catch (e) {
      set({ error: (e as Error).message });
    }
  },

  refreshQuotes: async (items?: string[] | StockBasic[]) => {
    set({ loading: true, error: null });
    try {
      const pool = get().pool;
      const prev = get().quotes;

      // 1. 规范化 items 为 StockBasic[]
      //    - 传字符串数组（兼容旧调用）→ 用 pool + 已缓存 quotes 里的 basic 补全
      //    - 传 StockBasic[]（搜索补全等场景）→ 直接用
      let targetBasics: StockBasic[];
      if (!items || items.length === 0) {
        // 默认：pool + 已有 quotes 里所有股票（之前 refreshQuotes 自己也会合并）
        const fromQuotes: StockBasic[] = Object.values(prev)
          .filter((q) => !pool.find((p) => p.code === q.code))
          .map((q) => ({
            code: q.code,
            name: q.name,
            market: q.market,
            type: q.type,
            tag: q.type === 'crypto' ? '加密货币' : 'A 股',
          }));
        targetBasics = [...pool, ...fromQuotes];
      } else if (typeof items[0] === 'string') {
        // 字符串数组：用 pool + 已缓存 quotes 反查 basic
        const codes = items as string[];
        const fromQuotes: StockBasic[] = Object.values(prev)
          .filter((q) => codes.includes(q.code) && !pool.find((p) => p.code === q.code))
          .map((q) => ({
            code: q.code,
            name: q.name,
            market: q.market,
            type: q.type,
            tag: q.type === 'crypto' ? '加密货币' : 'A 股',
          }));
        targetBasics = [
          ...pool.filter((p) => codes.includes(p.code)),
          ...fromQuotes,
        ];
      } else {
        // 完整 StockBasic[]（搜索补全场景直接传 basic）
        targetBasics = items as StockBasic[];
      }

      if (targetBasics.length === 0) {
        set({ loading: false });
        return [];
      }

      // 调后端真实行情
      const r = await api.getRealtimeQuotes(
        targetBasics.map((b) => ({ code: b.code, market: b.market, name: b.name }))
      );

      const next: Record<string, StockQuote> = { ...prev };
      const now = Date.now();

      // 真实行情覆盖对应标的
      for (const q of r.quotes) {
        const basic =
          targetBasics.find((b) => b.code === q.code) ||
          pool.find((p) => p.code === q.code);
        if (!basic) continue;
        // 如果接口没返回涨跌，就用现价 vs 昨收计算
        const changePercent =
          q.changePercent ??
          (q.prevClose > 0 ? ((q.price - q.prevClose) / q.prevClose) * 100 : 0);
        const oldQ = prev[q.code];
        next[q.code] = {
          code: q.code,
          name: q.name || basic.name,
          market: q.market,
          type: q.type,
          price: q.price,
          open: q.open || (oldQ?.open ?? q.price),
          prevClose: q.prevClose || (oldQ?.prevClose ?? q.price),
          high: q.high || (oldQ?.high ?? q.price),
          low: q.low || (oldQ?.low ?? q.price),
          changePercent,
          volume24h: q.volume24h,
          timestamp: q.timestamp || now,
        };
      }

      set({ quotes: next, lastUpdate: now });
      return Object.values(next).filter((q) =>
        targetBasics.some((b) => b.code === q.code)
      );
    } catch (e) {
      set({ error: (e as Error).message });
      return [];
    } finally {
      set({ loading: false });
    }
  },

  signin: async () => {
    const r = await api.signin();
    set({ account: r.account });
  },

  reset: async () => {
    const r = await api.reset();
    set({ account: r.account });
  },

  buy: wrapAction(api.buy, set),
  sell: wrapAction(api.sell, set),
  buyInsurance: wrapAction(api.buyBasic, set),
  buyFinanceInsurance: wrapAction((tier) => api.buyFinance(tier), set),
  buyGuard: wrapAction(api.buyGuard, set),
  setAutoInsurance: wrapAction(api.setAutoInsurance, set),

  // Contract
  openContract: wrapAction(api.openContract, set),
  closeContract: wrapAction((cid: string, price: number) => api.closeContract(cid, price), set),
  setContractTPSL: wrapAction((cid: string, tp?: number, sl?: number) => api.setContractTPSL(cid, tp, sl), set),

  triggerHeige: async (body) => {
    const r = await api.triggerHeige(body);
    return r.result;
  },

  getHeigeHistory: async () => {
    const r = await api.getHeigeHistory();
    return (r.events ?? []).map((e: any) => ({
      id: e.id,
      type: e.event_type,
      label: e.label,
      severity: e.severity,
      timestamp: e.occurred_at,
    })) as HeigeEvent[];
  },

  // web 端无手动 poll 入口 — 服务端驱动 + SSE 推送；这里返回 null 占位
  pollHeigeFeed: async () => null,

  onHeigeEvent: (cb) => {
    // SSE 订阅：事件到达时拉一次自己的 impact 详情
    return subscribeHeigeStream(async (ev: HeigeStreamEvent) => {
      // 服务端已经应用事件到所有用户，client 只需要刷新账户 + 显示通知
      try {
        // 拉自己的 impact 详情（最新一条 matching eventId）
        const impactsR = await api.getHeigeImpacts();
        const myImpact = (impactsR.impacts ?? []).find((i: any) => i.eventId === ev.id);

        // mock 一个 result 形状给 cb
        const result: HeigeTriggerResult = {
          event: {
            id: ev.id,
            type: ev.type as any,
            label: ev.label,
            severity: ev.severity,
            timestamp: ev.timestamp,
          },
          blockByGuard: myImpact?.blockedByGuard ?? false,
          affected: myImpact?.affected ?? [],
          task2Reward: myImpact?.task2Reward ?? 0,
          totalPayout: myImpact?.totalPayout ?? 0,
        };

        cb({ event: result.event, result });
      } catch (err) {
        console.warn('[sse] fetch impact failed:', err);
      }
    });
  },

  startTicker: (intervalMs = 10000) => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      const { pool, refreshQuotes, loadMarketStatus } = get();
      if (pool.length > 0) {
        await refreshQuotes(pool.map((s) => s.code));
      }
      await loadMarketStatus();
    };
    tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  },

  refreshNewsHealth: async () => {
    try {
      const resp = await fetch(SSE_CONFIG.recentUrl());
      if (resp.ok) {
        const data = await resp.json();
        set({
          newsHealth: {
            status: 'ok',
            lastSuccessAt: Date.now(),
            lastErrorAt: null,
            lastErrorMessage: null,
            consecutiveFailures: 0,
          },
        });
      }
    } catch {
      // 静默
    }
  },

  // ============ Web 版简化 storeInfo ============
  loadStoreInfo: async () => {
    set({
      storeInfo: {
        path: 'Cloudflare D1 + KV',
        isCustom: false,
        sizeBytes: 0,
      },
    });
  },

  pickStorePath: async () => null,

  migrateStore: async () => ({ ok: false, message: '网页版数据存储在云端，无需迁移' }),

  resetStorePath: async () => {},
}));